import { Fragment } from 'react';

import { cn, ia, iaRa } from 'lib/helpers/utility';

import DocText from '../DocText';
import { DOC_TEXT } from '../lib/docStyles';

const alignClass = (align) =>
  align === 'right' ? 'flex flex-col items-end text-right' : 'text-left';

const labelLines = (label) => (ia(label) ? label : [label]);

const CellText = ({ column, cell, row, index, sizeClass }) => (
  <DocText
    size="xs"
    className={cn(
      sizeClass,
      alignClass(cell.align ?? column.align),
      (column.strong || cell.strong) && 'font-700',
      (column.tabular || cell.tabular) && cell.tabular !== false && 'tabular-nums',
      (column.align === 'right' || column.nowrap || cell.nowrap) && 'whitespace-nowrap'
    )}>
    {cell.value}
    {index === 0 && row.titleSuffix ? (
      <span className="font-400 text-neutral-500"> · {row.titleSuffix}</span>
    ) : null}
  </DocText>
);

/**
 * @param {object} params
 * @param {Array<{label: string|string[], align?: 'left'|'right', width?: string, strong?: boolean, tabular?: boolean, nowrap?: boolean}>} params.columns
 * @param {Array<{key?: string, heading?: string, date?: string, note?: string, rows: Array<{cells: Array<{value: any}>, titleSuffix?: string, subLines?: string[], tint?: boolean}>}>} params.groups
 * @param {Array<{value: any, colSpan?: number, align?: string}>} [params.totals]
 * @param {boolean} [params.strongTotals] - Totals row closed by the design's 2px rule.
 * @param {string} [params.rowPadClass] - Row vertical padding.
 * @param {string} [params.headerPadClass] - Header cell bottom padding.
 * @param {string} [params.gutterClass] - Horizontal padding on every column but
 * @param {string} [params.firstGutterClass] - Right padding on the description column.
 */
const DocTable = ({
  columns = [],
  groups = [],
  totals,
  strongTotals = false,
  rowPadClass = 'py-1.5',
  headerPadClass = 'pb-1.5',
  gutterClass,
  firstGutterClass = '',
  className
}) => {
  const columnCount = columns.length;

  if (!columnCount) return null;

  const gutterFor = (index) => {
    if (index === 0) return firstGutterClass;
    if (gutterClass) return gutterClass;
    return index === columnCount - 1 ? '' : 'px-1';
  };

  return (
    <table className={cn('w-full table-fixed border-collapse', className)}>
      <colgroup>
        {columns.map((column, index) => (
          <col key={`${column.key ?? index}`} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((column, index) => (
            <th
              key={`${column.key ?? index}`}
              scope="col"
              className={cn(
                'border-0 border-b border-solid border-neutral-900 align-bottom',
                headerPadClass,
                gutterFor(index)
              )}>
              <DocText
                variant="label"
                size="small"
                className={cn(DOC_TEXT.columnLabel, alignClass(column.align))}>
                {labelLines(column.label).map((line, lineIndex) => (
                  <Fragment key={`label-${lineIndex}`}>
                    {lineIndex > 0 && <br />}
                    {String(line ?? '').toUpperCase()}
                  </Fragment>
                ))}
              </DocText>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {iaRa(groups).map((group, groupIndex) => (
          <Fragment key={group.key ?? `group-${groupIndex}`}>
            {(group.heading || group.date) && (
              <tr data-doc-unit className="bg-neutral-100">
                <td
                  colSpan={columnCount}
                  className="border-0 border-b border-solid border-neutral-300 px-2 py-[5px]">
                  <DocText variant="label" size="small" className={DOC_TEXT.groupLabel}>
                    {[group.heading, group.date].filter(Boolean).join(' · ').toUpperCase()}
                    {group.note && (
                      <span
                        className={cn(DOC_TEXT.groupNote, 'font-400 normal-case tracking-normal')}>
                        {' · '}
                        {group.note}
                      </span>
                    )}
                  </DocText>
                </td>
              </tr>
            )}
            {iaRa(group.rows).map((row, rowIndex) => (
              <tr
                data-doc-unit
                key={row.key ?? `${group.key ?? groupIndex}-row-${rowIndex}`}
                className={cn(row.tint && 'bg-neutral-50')}>
                {columns.map((column, index) => {
                  const cell = row.cells?.[index] || {};

                  return (
                    <td
                      key={`${column.key ?? index}`}
                      className={cn(
                        'border-0 border-b border-solid align-top',
                        row.tint
                          ? 'border-neutral-200 py-1'
                          : cn('border-neutral-300', rowPadClass),
                        gutterFor(index),
                        row.tint && index === 0 && 'pl-4'
                      )}>
                      <CellText
                        column={column}
                        cell={cell}
                        row={row}
                        index={index}
                        sizeClass={row.tint ? DOC_TEXT.cellTint : DOC_TEXT.cell}
                      />
                      {index === 0 &&
                        iaRa(row.subLines).map((line, lineIndex) => (
                          <DocText key={`sub-${lineIndex}`} size="xs" className={DOC_TEXT.cellSub}>
                            {line}
                          </DocText>
                        ))}
                      {iaRa(cell.subLines).map((line, lineIndex) => (
                        <DocText
                          key={`cell-sub-${lineIndex}`}
                          size="xs"
                          className={cn(DOC_TEXT.cellSub, alignClass(cell.align ?? column.align))}>
                          {line}
                        </DocText>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </Fragment>
        ))}
        {iaRa(totals).length > 0 && (
          <tr>
            {iaRa(totals).map((total, index) => (
              <td
                key={`total-${index}`}
                colSpan={total.colSpan}
                className={cn(
                  rowPadClass,
                  gutterFor(index),
                  strongTotals && 'border-0 border-t-2 border-solid border-neutral-900'
                )}>
                <DocText
                  size="xs"
                  className={cn(
                    strongTotals ? DOC_TEXT.totalsCell : DOC_TEXT.cell,
                    'whitespace-nowrap',
                    !strongTotals && 'font-700',
                    alignClass(total.align),
                    total.tabular !== false && 'tabular-nums'
                  )}>
                  {total.value}
                </DocText>
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default DocTable;
